// src/shared/ui/Tooltip.jsx
//
// Usage:
//   <Tooltip content="Full error message here">
//     <Badge>Failed</Badge>
//   </Tooltip>
//
// `content` falsy (null/undefined/"") renders just the trigger, no hover
// wiring -- so a caller can pass a conditional reason without an extra
// branch at the call site.

import { useRef, useState } from "react";
import { createPortal } from "react-dom";
import PropTypes from "prop-types";

const TOOLTIP_WIDTH = 256;           
const TOOLTIP_HEIGHT_ESTIMATE = 90;  
const TOOLTIP_GAP = 6;               
const TOOLTIP_VIEWPORT_MARGIN = 8;   

export default function Tooltip({ content, children, className = "" }) {
  const anchorRef = useRef(null);
  const [coords, setCoords] = useState(null); 

  function show() {
    const rect = anchorRef.current?.getBoundingClientRect();
    if (!rect) return;
    const spaceBelow = window.innerHeight - rect.bottom;
    const top = spaceBelow < TOOLTIP_HEIGHT_ESTIMATE
      ? rect.top - TOOLTIP_HEIGHT_ESTIMATE - TOOLTIP_GAP
      : rect.bottom + TOOLTIP_GAP;
    const left = Math.min(rect.left, window.innerWidth - TOOLTIP_WIDTH - TOOLTIP_VIEWPORT_MARGIN);
    setCoords({ top, left: Math.max(left, TOOLTIP_VIEWPORT_MARGIN) });
  }
  function hide() {
    setCoords(null);
  }

  if (!content) return <>{children}</>;

  return (
    <>
      <span
        ref={anchorRef}
        className={`inline-flex items-center ${className}`.trim()}
        onMouseEnter={show}
        onMouseLeave={hide}
      >
        {children}
      </span>
      {coords && createPortal(
        <div
          className="fixed z-50 w-64 rounded-card border border-line bg-white text-ink text-xs px-3 py-2 shadow-card text-left font-normal leading-relaxed break-words"
          style={{ top: coords.top, left: coords.left }}
        >
          {content}
        </div>,
        document.body
      )}
    </>
  );
}

Tooltip.propTypes = {
  content: PropTypes.node,
  children: PropTypes.node.isRequired,
  className: PropTypes.string,
};
